<template>
  <div
    class="timeline-component"
    :data-segment-count="availabilitySegments.length"
    :data-ai-alert-count="aiAlerts.length"
    :data-load-error="loadError ? 'true' : 'false'"
  >
    <div ref="timeline" class="timeline-canvas"></div>
    <div class="recording-segments-layer" aria-hidden="true">
      <div
        v-for="bar in recordingBars"
        :key="bar.id"
        class="recording-segment"
        :style="bar.style"
      ></div>
    </div>
    <div class="ai-alerts-layer">
      <button
        v-for="marker in aiAlertMarkers"
        :key="marker.id"
        type="button"
        class="ai-alert-marker"
        :style="marker.style"
        :title="marker.label"
        :aria-label="marker.label"
        :data-alert-label="marker.label"
        @click.stop="selectAiAlert(marker.alert)"
        @mouseenter="showAiAlertTooltip($event, marker.label)"
        @mouseleave="hideTimelineTooltip"
        @focus="showAiAlertTooltip($event, marker.label)"
        @blur="hideTimelineTooltip"
      ></button>
    </div>
    <div v-if="selectionMode && selectionBar" class="download-selection-range" :style="selectionBar"></div>
    <div ref="hoverLine" class="hover-line"></div>
    <div ref="tooltip" class="hover-tooltip"></div>
    <div v-if="loading" class="timeline-status">Carregando períodos...</div>
    <div v-else-if="loadError" class="timeline-status timeline-status-error">
      Não foi possível carregar os períodos de gravação.
    </div>
    <div v-else-if="alertsLoadError" class="timeline-status timeline-status-warning">
      Alertas IA indisponíveis.
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Timeline, DataSet } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import moment from 'moment';
import 'moment/locale/pt-br';
import api from '../../config/axios.js';

const props = defineProps({
  cameraToken: {
    type: String,
    required: true
  },
  date: {
    type: String,
    required: true
  },
  refreshKey: {
    type: Number,
    default: 0
  },
  selectionMode: {
    type: Boolean,
    default: false
  },
  selectionStart: {
    type: [String, Date],
    default: null
  },
  selectionEnd: {
    type: [String, Date],
    default: null
  }
});

const emit = defineEmits(['clickTimeLine', 'unavailableTime', 'selectionChange']);

const timeline = ref(null);
const hoverLine = ref(null);
const tooltip = ref(null);
const loading = ref(false);
const loadError = ref(false);
const alertsLoadError = ref(false);
const availabilitySegments = ref([]);
const recordingBars = ref([]);
const aiAlerts = ref([]);
const aiAlertMarkers = ref([]);
const selectionBar = ref(null);

let timelineInstance = null;
let itemsDataSet = null;
let mouseMoveHandler = null;
let mouseLeaveHandler = null;
let abortController = null;
let requestSequence = 0;
let bloqueiaCliqueTemporariamente = false;
let selectionMarkersAdded = false;
let resizeObserver = null;
let modalElement = null;
let modalShownHandler = null;
let layoutRefreshFrame = null;
let layoutRefreshTimeout = null;

function getDayBounds(date) {
  const [year, month, day] = date.split('-').map(Number);

  return {
    start: new Date(year, month - 1, day, 0, 0, 0, 0),
    end: new Date(year, month - 1, day + 1, 0, 0, 0, 0)
  };
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatTimelineDateTime(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

function isToday(date) {
  return date === formatLocalDate(new Date());
}

function isRecordedTime(time) {
  const timestamp = time.getTime();

  return availabilitySegments.value.some((segment) => (
    timestamp >= segment.start.getTime() && timestamp < segment.end.getTime()
  ));
}

function configureTimelineWindow() {
  if (!timelineInstance || !props.date) return;

  const { start: dayStart, end: dayEnd } = getDayBounds(props.date);
  const selectedEnd = props.selectionMode && props.selectionEnd ? new Date(props.selectionEnd) : null;
  let start = dayStart;
  let end = selectedEnd && !Number.isNaN(selectedEnd.getTime()) && selectedEnd > dayEnd
    ? new Date(selectedEnd.getTime() + 60000)
    : dayEnd;

  if (props.selectionMode && props.selectionStart && props.selectionEnd) {
    const selectedStart = new Date(props.selectionStart);
    const selectionEnd = new Date(props.selectionEnd);

    if (!Number.isNaN(selectedStart.getTime()) && !Number.isNaN(selectionEnd.getTime()) && selectionEnd > selectedStart) {
      const focusPadding = 5 * 60 * 1000;
      start = new Date(selectedStart.getTime() - focusPadding);
      end = new Date(selectionEnd.getTime() + focusPadding);
    }
  }

  const showCurrentTime = isToday(props.date);

  timelineInstance.setOptions({ showCurrentTime });
  timelineInstance.setWindow(start, end, { animation: false });

  if (showCurrentTime) {
    timelineInstance.setCurrentTime(new Date());
  }

  updateRecordingBars();
  syncSelectionMarkers();
}

function updateRecordingBars() {
  if (!timelineInstance) {
    recordingBars.value = [];
    aiAlertMarkers.value = [];
    return;
  }

  const windowRange = timelineInstance.getWindow();
  const windowStart = windowRange.start.getTime();
  const windowEnd = windowRange.end.getTime();
  const windowDuration = windowEnd - windowStart;
  const timelineRect = timeline.value?.getBoundingClientRect();
  const visualTimelineRect = timeline.value?.querySelector('.vis-timeline')?.getBoundingClientRect();
  const centerPanelRect = timeline.value?.querySelector('.vis-panel.vis-center')?.getBoundingClientRect();

  if (
    windowDuration <= 0
    || !timelineRect
    || !visualTimelineRect
    || !centerPanelRect
    || timelineRect.width <= 0
    || centerPanelRect.width < Math.max(2, timelineRect.width * 0.5)
  ) {
    recordingBars.value = [];
    aiAlertMarkers.value = [];
    selectionBar.value = null;
    return;
  }

  const panelLeft = centerPanelRect.left - timelineRect.left;
  const panelWidth = centerPanelRect.width;
  const positionFor = (timestamp) => panelLeft + ((timestamp - windowStart) / windowDuration) * panelWidth;

  recordingBars.value = availabilitySegments.value.flatMap((segment, index) => {
    const visibleStart = Math.max(segment.start.getTime(), windowStart);
    const visibleEnd = Math.min(segment.end.getTime(), windowEnd);

    if (visibleEnd <= visibleStart) return [];

    return [{
      id: `recording-${index}`,
      style: {
        left: `${positionFor(visibleStart)}px`,
        width: `${positionFor(visibleEnd) - positionFor(visibleStart)}px`
      }
    }];
  });

  aiAlertMarkers.value = aiAlerts.value.flatMap((alert) => {
    const timestamp = alert.detectedAt.getTime();

    if (timestamp < windowStart || timestamp > windowEnd) return [];

    return [{
      id: `ai-alert-${alert.id}`,
      alert,
      label: formatAiAlertLabel(alert),
      style: {
        left: `${positionFor(timestamp)}px`
      }
    }];
  });

  if (props.selectionMode && props.selectionStart && props.selectionEnd) {
    const start = Math.max(new Date(props.selectionStart).getTime(), windowStart);
    const end = Math.min(new Date(props.selectionEnd).getTime(), windowEnd);
    const selectionTop = Math.max(0, visualTimelineRect.top - timelineRect.top + 7);
    const selectionHeight = Math.max(0, visualTimelineRect.bottom - timelineRect.top - selectionTop);
    selectionBar.value = end > start ? {
      left: `${positionFor(start)}px`,
      width: `${positionFor(end) - positionFor(start)}px`,
      top: `${selectionTop}px`,
      height: `${selectionHeight}px`
    } : null;
  } else {
    selectionBar.value = null;
  }
}

function formatAiAlertLabel(alert) {
  const time = alert.detectedAt.toLocaleTimeString('pt-BR', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const confidence = Number.isFinite(alert.confidence)
    ? ` • confiança ${Math.round(alert.confidence * 100)}%`
    : '';

  return `Alerta IA às ${time}${confidence}`;
}

function selectAiAlert(alert) {
  const detectedAt = new Date(alert.detectedAt);
  const formattedDateTime = formatTimelineDateTime(detectedAt);

  if (isRecordedTime(detectedAt)) {
    emit('clickTimeLine', formattedDateTime);
    return;
  }

  emit('unavailableTime', formattedDateTime);
}

function showAiAlertTooltip(event, label) {
  if (!timeline.value || !hoverLine.value || !tooltip.value) return;

  const timelineRect = timeline.value.getBoundingClientRect();
  const markerRect = event.currentTarget.getBoundingClientRect();
  const x = markerRect.left + (markerRect.width / 2) - timelineRect.left;

  hoverLine.value.style.left = `${x}px`;
  hoverLine.value.style.display = 'block';
  tooltip.value.style.left = `${x + 5}px`;
  tooltip.value.style.top = '5px';
  tooltip.value.innerText = label;
  tooltip.value.style.display = 'block';
}

function hideTimelineTooltip() {
  if (!hoverLine.value || !tooltip.value) return;

  hoverLine.value.style.display = 'none';
  tooltip.value.style.display = 'none';
}

function refreshTimelineLayout() {
  if (!timelineInstance || !timeline.value) return;

  timelineInstance.redraw();
  updateRecordingBars();
  syncSelectionMarkers();
}

function scheduleTimelineLayoutRefresh() {
  if (layoutRefreshFrame !== null) {
    cancelAnimationFrame(layoutRefreshFrame);
  }

  layoutRefreshFrame = requestAnimationFrame(() => {
    layoutRefreshFrame = requestAnimationFrame(() => {
      layoutRefreshFrame = null;
      refreshTimelineLayout();
    });
  });

  // O modal do Bootstrap leva alguns milissegundos para concluir a transição.
  // Este segundo passe garante que o vis-timeline seja redesenhado já com largura.
  if (layoutRefreshTimeout === null) {
    layoutRefreshTimeout = setTimeout(() => {
      layoutRefreshTimeout = null;
      refreshTimelineLayout();
    }, 350);
  }
}

function syncSelectionMarkers() {
  if (!timelineInstance) return;

  if (!props.selectionMode || !props.selectionStart || !props.selectionEnd) {
    if (selectionMarkersAdded) {
      timelineInstance.removeCustomTime('download-start');
      timelineInstance.removeCustomTime('download-end');
      selectionMarkersAdded = false;
    }
    selectionBar.value = null;
    return;
  }

  const start = new Date(props.selectionStart);
  const end = new Date(props.selectionEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

  if (!selectionMarkersAdded) {
    timelineInstance.addCustomTime(start, 'download-start');
    timelineInstance.addCustomTime(end, 'download-end');
    timelineInstance.setCustomTimeTitle('Inicio do trecho', 'download-start');
    timelineInstance.setCustomTimeTitle('Fim do trecho', 'download-end');
    selectionMarkersAdded = true;
  } else {
    timelineInstance.setCustomTime(start, 'download-start');
    timelineInstance.setCustomTime(end, 'download-end');
  }
  updateRecordingBars();
}

function emitSelection(changedId, changedTime) {
  let start = new Date(props.selectionStart);
  let end = new Date(props.selectionEnd);
  const time = new Date(changedTime);

  if (changedId === 'download-start') start = time;
  if (changedId === 'download-end') end = time;
  if (end <= start) {
    if (changedId === 'download-start') end = new Date(start.getTime() + 1000);
    else start = new Date(end.getTime() - 1000);
  }
  if (end.getTime() - start.getTime() > 600000) {
    if (changedId === 'download-start') end = new Date(start.getTime() + 600000);
    else start = new Date(end.getTime() - 600000);
  }
  if (end > new Date()) end = new Date();

  emit('selectionChange', { start, end });
}

function updateItems(segments) {
  availabilitySegments.value = segments
    .map((segment) => ({
      start: new Date(segment.start),
      end: new Date(segment.end)
    }))
    .filter((segment) => (
      !Number.isNaN(segment.start.getTime())
      && !Number.isNaN(segment.end.getTime())
      && segment.end > segment.start
    ));

  itemsDataSet.clear();
  updateRecordingBars();
  scheduleTimelineLayoutRefresh();
}

function updateAiAlerts(alerts) {
  const now = Date.now();

  aiAlerts.value = alerts
    .map((alert) => ({
      id: alert.id,
      eventType: alert.event_type,
      confidence: alert.confidence === null ? null : Number(alert.confidence),
      detectedAt: new Date(alert.detected_at)
    }))
    .filter((alert) => (
      !Number.isNaN(alert.detectedAt.getTime())
      && (!isToday(props.date) || alert.detectedAt.getTime() <= now)
    ));

  updateRecordingBars();
  scheduleTimelineLayoutRefresh();
}

async function loadTimelineData() {
  if (!props.cameraToken || !props.date || !itemsDataSet) return;

  if (abortController) {
    abortController.abort();
  }

  abortController = new AbortController();
  const currentRequest = ++requestSequence;

  loading.value = true;
  loadError.value = false;
  alertsLoadError.value = false;
  availabilitySegments.value = [];
  recordingBars.value = [];
  aiAlerts.value = [];
  aiAlertMarkers.value = [];
  itemsDataSet.clear();
  configureTimelineWindow();

  try {
    const dates = [props.date];
    if (props.selectionMode && props.selectionEnd) {
      const endDate = formatLocalDate(new Date(props.selectionEnd));
      if (endDate !== props.date) dates.push(endDate);
    }
    const [availabilityResult, alertsResult] = await Promise.allSettled([
      Promise.all(dates.map((date) => api.axios.get(
        `/api/cameras/${encodeURIComponent(props.cameraToken)}/recording-availability`,
        { params: { date }, signal: abortController.signal }
      ))),
      Promise.all(dates.map((date) => api.axios.get(
        `/api/cameras/${encodeURIComponent(props.cameraToken)}/ai-alerts`,
        { params: { date }, signal: abortController.signal }
      )))
    ]);

    if (currentRequest !== requestSequence) return;

    if (availabilityResult.status === 'fulfilled') {
      updateItems(availabilityResult.value.flatMap(
        (response) => Array.isArray(response.data?.segments) ? response.data.segments : []
      ));
    } else {
      loadError.value = true;
      availabilitySegments.value = [];
      recordingBars.value = [];
      itemsDataSet.clear();
    }

    if (alertsResult.status === 'fulfilled') {
      updateAiAlerts(alertsResult.value.flatMap(
        (response) => Array.isArray(response.data?.alerts) ? response.data.alerts : []
      ));
    } else {
      alertsLoadError.value = true;
      aiAlerts.value = [];
      aiAlertMarkers.value = [];
    }
  } catch (error) {
    if (currentRequest !== requestSequence || error?.code === 'ERR_CANCELED') return;

    loadError.value = true;
    alertsLoadError.value = true;
    availabilitySegments.value = [];
    recordingBars.value = [];
    aiAlerts.value = [];
    aiAlertMarkers.value = [];
    itemsDataSet.clear();
  } finally {
    if (currentRequest === requestSequence) {
      loading.value = false;
    }
  }
}

onMounted(() => {
  itemsDataSet = new DataSet([]);

  const { start, end } = getDayBounds(props.date);
  const options = {
    start,
    end,
    zoomMin: 1000 * 10,
    zoomMax: 1000 * 60 * 60 * 48,
    editable: false,
    selectable: false,
    stack: false,
    horizontalScroll: true,
    showCurrentTime: isToday(props.date),
    orientation: 'bottom',
    format: {
      minorLabels: { hour: 'HH:mm' },
      majorLabels: { day: 'dddd D [de] MMMM' }
    },
    moment: (date) => moment(date)
  };

  timelineInstance = new Timeline(timeline.value, itemsDataSet, options);
  configureTimelineWindow();
  scheduleTimelineLayoutRefresh();

  modalElement = timeline.value.closest('.modal');
  if (modalElement) {
    modalShownHandler = () => scheduleTimelineLayoutRefresh();
    modalElement.addEventListener('shown.bs.modal', modalShownHandler);
  }

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => scheduleTimelineLayoutRefresh());
    resizeObserver.observe(timeline.value);

    const centerPanel = timeline.value.querySelector('.vis-panel.vis-center');
    if (centerPanel) {
      resizeObserver.observe(centerPanel);
    }
  }

  mouseMoveHandler = (event) => {
    if (!timeline.value || !hoverLine.value || !tooltip.value || !timelineInstance) return;

    const rect = timeline.value.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const alertLabel = event.target instanceof Element
      ? event.target.closest('.ai-alert-marker')?.dataset.alertLabel
      : null;

    if (alertLabel) {
      hoverLine.value.style.left = `${x}px`;
      hoverLine.value.style.display = 'block';
      tooltip.value.style.left = `${x + 5}px`;
      tooltip.value.style.top = '5px';
      tooltip.value.innerText = alertLabel;
      tooltip.value.style.display = 'block';
      return;
    }

    let eventProperties = null;

    try {
      eventProperties = timelineInstance.getEventProperties(event);
    } catch (error) {
      return;
    }

    if (!eventProperties?.time) return;

    const time = new Date(eventProperties.time).toLocaleTimeString('pt-BR', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    hoverLine.value.style.left = `${x}px`;
    hoverLine.value.style.display = 'block';
    tooltip.value.style.left = `${x + 5}px`;
    tooltip.value.style.top = '5px';
    tooltip.value.innerText = time;
    tooltip.value.style.display = 'block';
  };

  mouseLeaveHandler = hideTimelineTooltip;

  timeline.value.addEventListener('mousemove', mouseMoveHandler);
  timeline.value.addEventListener('mouseleave', mouseLeaveHandler);

  timelineInstance.on('rangechange', () => {
    updateRecordingBars();
    bloqueiaCliqueTemporariamente = true;

    setTimeout(() => {
      bloqueiaCliqueTemporariamente = false;
    }, 500);
  });

  timelineInstance.on('timechange', (properties) => {
    if (!props.selectionMode || !String(properties.id).startsWith('download-')) return;
    emitSelection(properties.id, properties.time);
  });

  timelineInstance.on('timechanged', (properties) => {
    if (!props.selectionMode || !String(properties.id).startsWith('download-')) return;
    emitSelection(properties.id, properties.time);
  });

  timelineInstance.on('click', (eventProperties) => {
    if (bloqueiaCliqueTemporariamente || !eventProperties.time) return;

    const clickedDate = new Date(eventProperties.time);
    if (props.selectionMode) {
      const start = new Date(props.selectionStart);
      const end = new Date(props.selectionEnd);
      emitSelection(Math.abs(clickedDate - start) <= Math.abs(clickedDate - end) ? 'download-start' : 'download-end', clickedDate);
      return;
    }
    const formattedDateTime = formatTimelineDateTime(clickedDate);

    if (isToday(props.date)) {
      timelineInstance.setCurrentTime(clickedDate);
    }

    if (isRecordedTime(clickedDate)) {
      emit('clickTimeLine', formattedDateTime);
      return;
    }

    emit('unavailableTime', formattedDateTime);
  });

  loadTimelineData();
});

watch(
  () => [props.cameraToken, props.date, props.refreshKey, props.selectionMode],
  () => loadTimelineData()
);

watch(
  () => [props.selectionStart, props.selectionEnd],
  () => {
    syncSelectionMarkers();
    updateRecordingBars();
  }
);

onBeforeUnmount(() => {
  requestSequence += 1;

  if (layoutRefreshFrame !== null) {
    cancelAnimationFrame(layoutRefreshFrame);
    layoutRefreshFrame = null;
  }
  if (layoutRefreshTimeout !== null) {
    clearTimeout(layoutRefreshTimeout);
    layoutRefreshTimeout = null;
  }

  if (modalElement && modalShownHandler) {
    modalElement.removeEventListener('shown.bs.modal', modalShownHandler);
  }
  modalElement = null;
  modalShownHandler = null;

  if (abortController) {
    abortController.abort();
    abortController = null;
  }

  if (timeline.value && mouseMoveHandler) {
    timeline.value.removeEventListener('mousemove', mouseMoveHandler);
  }

  if (timeline.value && mouseLeaveHandler) {
    timeline.value.removeEventListener('mouseleave', mouseLeaveHandler);
  }

  if (timelineInstance) {
    timelineInstance.destroy();
    timelineInstance = null;
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  itemsDataSet = null;
});
</script>

<style scoped>
.timeline-component {
  position: relative;
  width: 100%;
  min-height: 80px;
  background-color: #fff;
}

.timeline-canvas {
  min-height: 80px;
}

.recording-segments-layer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  width: 100%;
  height: 7px;
  overflow: hidden;
  pointer-events: none;
}

.recording-segment {
  position: absolute;
  top: 0;
  height: 7px;
  background-color: #28a745;
}

.ai-alerts-layer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 7;
  width: 100%;
  height: 17px;
  pointer-events: none;
}

.ai-alert-marker {
  position: absolute;
  top: 0;
  width: 4px;
  height: 17px;
  padding: 0;
  border: 1px solid #fff;
  border-top: 0;
  border-radius: 0 0 2px 2px;
  background-color: #dc3545;
  box-shadow: 0 0 0 1px rgba(153, 27, 27, 0.35);
  cursor: pointer;
  pointer-events: auto;
  transform: translateX(-50%);
}

.ai-alert-marker:focus-visible {
  outline: 2px solid #5d5fef;
  outline-offset: 2px;
}

.download-selection-range {
  position: absolute;
  z-index: 5;
  background: rgba(93, 95, 239, 0.2);
  pointer-events: none;
}

:deep(.vis-custom-time) {
  z-index: 9;
  width: 3px;
  background-color: #5d5fef;
}

.timeline-status {
  position: absolute;
  top: 5px;
  right: 8px;
  z-index: 12;
  padding: 2px 6px;
  border-radius: 4px;
  color: #495057;
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  pointer-events: none;
}

.timeline-status-error {
  color: #b42318;
}

.timeline-status-warning {
  color: #9a6700;
}

:deep(.vis-timeline) {
  border-color: #ced4da;
  background-color: #fff;
}

:deep(.vis-panel.vis-background) {
  background-color: #fff;
}

:deep(.vis-panel.vis-center) {
  background-color: #e9ecef;
}

:deep(.vis-time-axis .vis-text) {
  color: #495057;
}

</style>
